import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import type { RagAssistantMessage, RagMessage, RagUserMessage } from "../../types/ragChatTypes";
import { isAssistantMessage } from "../../types/ragChatTypes";
import { getInitialMessages, ragId } from "../../data/mockRagData";
import {
  getRagSessionId,
  RAG_CHAT_API,
  resetRagSessionId,
  type RagChatQueryRequest,
  type RagChatQueryResponse,
} from "../../services/ragChatApi";
import {
  createChatErrorAssistantMessage,
  mapQueryResponseToAssistantMessage,
} from "../../utils/mapQueryResponse";
import { RagAssistantMessageCard } from "./RagAssistantMessageCard";
import { useAPI } from "../../../../shared/services/api/API";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";

function UserBubble({ content }: { content: string }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
      <Box
        className="rag-user-bubble"
        sx={{
          maxWidth: "80%",
          borderRadius: "16px 16px 4px 16px",
          bgcolor: "primary.main",
          color: "primary.contrastText",
          px: 2,
          py: 1.25,
        }}
      >
        <Typography variant="bodyNormal">{content}</Typography>
      </Box>
    </Box>
  );
}

type ComposerMode = "hero" | "dock";

const COMPOSER_BAR_HEIGHT_PX = 68;

export type RagChatThreadHandle = {
  startNewChat: () => void;
};

function ChatComposer({
  mode,
  input,
  loading,
  onInputChange,
  onSubmit,
  t,
  composerRef,
}: {
  mode: ComposerMode;
  input: string;
  loading: boolean;
  onInputChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  t: (k: string) => string;
  composerRef?: React.Ref<HTMLFormElement>;
}) {
  const isHero = mode === "hero";
  const isDock = mode === "dock";

  const theme = useTheme();
  const body = theme.typography.bodyNormal;

  const alignCrossAxis =
    input.includes("\n") || input.length > 160 ? ("flex-end" as const) : ("center" as const);

  const shellSx = {
    display: "flex",
    flexDirection: "row" as const,
    flexWrap: "nowrap" as const,
    alignItems: alignCrossAxis,
    gap: 1.25,
    px: 2,
    py: isDock ? 1 : 1.25,
    borderRadius: "28px",
    border: "1px solid",
    borderColor: isDock ? "rgba(15, 23, 42, 0.1)" : "rgba(15, 23, 42, 0.14)",
    bgcolor: "#ffffff",
    boxShadow: isDock
      ? "0 2px 16px rgba(15, 23, 42, 0.07), 0 1px 4px rgba(15, 23, 42, 0.04)"
      : "0 0 0 1px rgba(15, 23, 42, 0.04), 0 6px 24px rgba(15, 23, 42, 0.1), 0 2px 8px rgba(0, 132, 244, 0.06)",
    boxSizing: "border-box" as const,
    transition: "box-shadow 0.2s ease, border-color 0.2s ease",
    "&:focus-within": {
      borderColor: "primary.main",
      boxShadow: (theme: import("@mui/material").Theme) =>
        isDock
          ? `0 0 0 1px ${theme.palette.primary.main}33, 0 6px 22px rgba(15, 23, 42, 0.1)`
          : `0 0 0 1px ${theme.palette.primary.main}40, 0 10px 36px rgba(15, 23, 42, 0.12)`,
    },
  };

  return (
    <Paper
      ref={composerRef}
      component="form"
      className={
        isHero
          ? "rag-chat-input-dock rag-chat-input-dock--hero"
          : "rag-chat-input-dock rag-chat-input-dock--dock"
      }
      elevation={0}
      onSubmit={onSubmit}
      sx={{
        ...shellSx,
        minHeight: COMPOSER_BAR_HEIGHT_PX,
        width: "100%",
      }}
    >
      <TextField
        fullWidth
        multiline
        maxRows={6}
        minRows={1}
        placeholder={t("opva.chatAssistancePlaceholder")}
        value={input}
        onChange={(e) => onInputChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) return;
          e.preventDefault();
          if (loading || !input.trim()) return;
          (e.target as HTMLElement).closest("form")?.requestSubmit();
        }}
        disabled={loading}
        variant="standard"
        InputProps={{
          disableUnderline: true,
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          "& .MuiInputBase-root": {
            py: 0,
            px: 0.5,
            alignItems: "stretch",
            fontSize: body.fontSize,
            lineHeight: body.lineHeight,
            fontFamily: body.fontFamily,
            fontWeight: body.fontWeight,
            color: "text.primary",
          },
          "& .MuiInputBase-input": {
            fontSize: body.fontSize,
            lineHeight: body.lineHeight,
            fontFamily: body.fontFamily,
            fontWeight: body.fontWeight,
            paddingBlock: "11px",
            paddingInline: "2px",
            boxSizing: "border-box",
            resize: "none",
          },
          "& .MuiInputBase-input::placeholder": {
            color: "text.secondary",
            opacity: 1,
            fontSize: body.fontSize,
            lineHeight: body.lineHeight,
            fontFamily: body.fontFamily,
          },
        }}
      />
      <IconButton
        type="submit"
        color="primary"
        disabled={loading || !input.trim()}
        sx={{
          alignSelf: "flex-end",
          flexShrink: 0,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          width: 44,
          height: 44,
          boxShadow: "0 2px 6px rgba(0, 132, 244, 0.35)",
          "&:hover": { bgcolor: "primary.dark" },
          "&.Mui-disabled": {
            bgcolor: "action.disabledBackground",
            color: "action.disabled",
            boxShadow: "none",
          },
        }}
        aria-label={t("opva.chatAssistanceSend")}
      >
        <SendOutlinedIcon sx={{ fontSize: 22 }} />
      </IconButton>
    </Paper>
  );
}

function isNearBottom(el: HTMLElement, threshold = 96): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
}

function chatErrorMessage(status: number, t: (k: string) => string): string {
  if (status === 502 || status === 503) {
    return t("opva.chatAssistanceErrorServiceUnavailable");
  }
  if (status >= 500) {
    return t("opva.chatAssistanceErrorServer");
  }
  if (status === 400) {
    return t("opva.chatAssistanceErrorBadRequest");
  }
  return t("opva.chatAssistanceErrorGeneric");
}

function scrollMessagesToBottom(el: HTMLElement, smooth: boolean) {
  el.scrollTo({
    top: el.scrollHeight,
    behavior: smooth ? "smooth" : "auto",
  });
}

export const RagChatThread = forwardRef<RagChatThreadHandle>(function RagChatThread(_props, ref) {
  const { t } = useTranslation();
  const { httpPost, httpDelete } = useAPI();
  const [messages, setMessages] = useState<RagMessage[]>(() => getInitialMessages());
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const workspaceRef = useRef<HTMLDivElement | null>(null);
  const composerRef = useRef<HTMLFormElement | null>(null);
  const shouldStickToBottomRef = useRef(true);
  const sessionIdRef = useRef(getRagSessionId());
  const lastUserQueryRef = useRef("");

  const hasUserMessage = messages.some((m) => m.role === "user");

  useEffect(() => {
    const scrollEl = workspaceRef.current;
    if (scrollEl) {
      if (shouldStickToBottomRef.current || !hasUserMessage) {
        scrollMessagesToBottom(scrollEl, hasUserMessage);
      }
      return;
    }
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, loading, hasUserMessage]);

  const pushAssistant = (payload: RagAssistantMessage) => {
    setMessages((prev) => [...prev, payload]);
  };

  const submitQuery = async (
    text: string,
    opts?: {
      isSuggestion?: boolean;
      isOptionSelection?: boolean;
      targetPdf?: string;
      skipUserMessage?: boolean;
    }
  ) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    if (!opts?.skipUserMessage) {
      const userMsg: RagUserMessage = {
        id: ragId("user"),
        role: "user",
        content: trimmed,
      };
      setMessages((prev) => [...prev, userMsg]);
    }

    if (!opts?.isSuggestion && !opts?.isOptionSelection && !opts?.targetPdf) {
      lastUserQueryRef.current = trimmed;
    }

    setLoading(true);

    const body: RagChatQueryRequest = {
      query: trimmed,
      sessionId: sessionIdRef.current,
      isSuggestion: opts?.isSuggestion ?? false,
      isOptionSelection: opts?.isOptionSelection ?? false,
      targetPdf: opts?.targetPdf ?? "",
    };

    try {
      const response = await httpPost(RAG_CHAT_API.query, body, undefined, false);
      if (response.status >= 400) {
        pushAssistant(createChatErrorAssistantMessage(chatErrorMessage(response.status, t)));
        return;
      }
      const data = response.data as RagChatQueryResponse;
      if (data.sessionId) {
        sessionIdRef.current = data.sessionId;
        sessionStorage.setItem("rag_session_id", data.sessionId);
      }
      pushAssistant(mapQueryResponseToAssistantMessage(data));
    } catch {
      pushAssistant(
        createChatErrorAssistantMessage(
          t("opva.chatAssistanceErrorServiceUnavailable")
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    void submitQuery(text);
  };

  const handleNewChat = useCallback(() => {
    const oldSession = sessionIdRef.current;
    sessionIdRef.current = resetRagSessionId();
    lastUserQueryRef.current = "";
    setMessages(getInitialMessages());
    setSelectedOptions({});
    setInput("");
    setLoading(false);
    if (oldSession) {
      void httpDelete(RAG_CHAT_API.session(oldSession), undefined, undefined, false);
    }
  }, [httpDelete]);

  useImperativeHandle(ref, () => ({ startNewChat: handleNewChat }), [handleNewChat]);

  const handleOptionSelect = (msgId: string, opt: string) => {
    if (loading) return;
    setSelectedOptions((prev) => ({ ...prev, [msgId]: opt }));
    void submitQuery(opt, { isOptionSelection: true });
  };

  return (
    <Box className="rag-chat-layout" data-chat-phase={hasUserMessage ? "active" : "empty"}>
      <Box
        ref={workspaceRef}
        component="section"
        aria-label={t("opva.chatAssistance")}
        className="rag-chat-workspace"
        onScroll={(e) => {
          shouldStickToBottomRef.current = isNearBottom(e.currentTarget);
        }}
      >
        <Box className="rag-chat-content-rail">
          <Stack spacing={3}>
            {messages.map((msg) => {
              if (msg.role === "user") {
                return <UserBubble key={msg.id} content={msg.content} />;
              }
              if (isAssistantMessage(msg)) {
                return (
                  <RagAssistantMessageCard
                    key={msg.id}
                    msg={msg}
                    selectedOption={selectedOptions[msg.id] ?? null}
                    loading={loading}
                    onOptionSelect={handleOptionSelect}
                    onRelatedSelect={(q) => {
                      void submitQuery(q, { isSuggestion: true });
                    }}
                    onAlternativePdf={(pdf) => {
                      const query = lastUserQueryRef.current.trim();
                      if (!query) return;
                      void submitQuery(query, { targetPdf: pdf, skipUserMessage: true });
                    }}
                  />
                );
              }
              return null;
            })}

            {loading && (
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "grey.50",
                  }}
                >
                  <CircularProgress size={18} thickness={5} />
                  <Typography variant="bodySmall" color="text.secondary">
                    {t("opva.chatAssistancePreparingAnswer")}
                  </Typography>
                </Box>
              </Box>
            )}

            <div ref={bottomRef} />
          </Stack>
        </Box>
      </Box>

      <Box className="rag-chat-composer-dock">
        <Box className="rag-chat-content-rail">
          <ChatComposer
            mode={hasUserMessage ? "dock" : "hero"}
            input={input}
            loading={loading}
            onInputChange={setInput}
            onSubmit={handleSubmit}
            t={t}
            composerRef={composerRef}
          />
        </Box>
      </Box>
    </Box>
  );
});
